<!--搜索总容器-->
<template>
  <div class="sp-cont">
    <slot />
  </div>
</template>

<script setup lang="ts">
/**
 * ### 搜索总容器
 */
import { onMounted, reactive, nextTick, ref, readonly } from 'vue';
import { shareData, protectedReactive } from '../core/SharedData';
import { getLayout, saveLayout, deleteLayout } from '../core/LayoutPersistent';
import { arrayMove } from '../core/Common';
import { cloneDeep, find, findIndex } from 'lodash-es';
import { PlainObject } from '../core/types';

// 属性定义
const props = withDefaults(
  defineProps<{
    /**
     * ### 查询面板名称
     * <warning> 请确保各个页面该名称不重复
     * <warning> 查询条件/按钮/表格列配置等信息的缓存会和该属性绑定
     */
    name: string;
    /**
     * ### 首次打开时是否自动执行查询
     */
    autoSearch?: boolean;
    /**
     * ### 表格数据查询前的钩子函数
     * 对于查询前参数处理等可在该钩子中做处理
     */
    // eslint-disable-next-line vue/require-default-prop
    before?: (sp: PlainObject) => void;
    /**
     * ### 表格数据查询完成后的钩子函数
     */
    // eslint-disable-next-line vue/require-default-prop
    after?: (sp: PlainObject) => void; // 查询后回调
    /**
     * ### 是否使用高级查询
     * <warning> 如果为高级查询则show字段不再影响组件的显示与隐藏 一直保持显示
     * <warning> 同时show为false则展示在高级查询种
     */
    useAdvancedSearch?: boolean;
  }>(),

  // 属性默认值
  {
    autoSearch: true
  }
);

// 暴露方法
defineExpose({
  /**
   * ### 表格数据重新请求方法
   */
  search: (keepPage?: boolean) => searchCommand(keepPage),
  /**
   * ### 获取当前查询参数方法
   */
  param: (name?: string) => {
    if (name) return searchParams[name];
    return { ...searchParams };
  },
  /**
   * ### 获取上一次查询参数方法
   */
  usedParams: () => readonly(prevSearchParams),
  /**
   * ### 设置查询参数方法
   * <warning> 若查询组件设置unset属性则对应的参数项设置不生效
   */
  setParams: (params: PlainObject, autoSearch?: boolean) => {
    return setAndQueryCommand(params, !autoSearch);
  }
});

// 查询参数
const searchParams = protectedReactive();

// 分页参数
const pagination = reactive({ page: 0, size: 50, showTotal: true });

// 由SearchTable注册下来，包含{execute:/*执行查询*/}
const searchTable = protectedReactive<{
  execute?: (sp: PlainObject) => void;
}>();

// 上一次的查询条件
const prevSearchParams = ref<PlainObject | undefined>();

const loading = ref(false);

const searchCommand = (keepPage?: boolean /*保存页码不变 */) => {
  // 页码复位
  if (!keepPage) {
    pagination.page = 1;
  }

  return nextTick(async () => {
    // 翻页时, 不能因为修改了查询条件就是使用新查询
    if (keepPage === true && prevSearchParams.value) {
      const sp: PlainObject = {};
      for (let key of Object.keys(searchParams)) {
        sp[key] = prevSearchParams.value[key];
      }

      await props.before?.(sp);
      try {
        loading.value = true;
        await searchTable.execute?.(sp);
      } finally {
        loading.value = false;
      }
      if (props.after) {
        await nextTick(async () => await props.after?.(sp));
      }
      return;
    }

    await props.before?.(searchParams);
    try {
      loading.value = true;
      await searchTable.execute?.(searchParams);
    } finally {
      loading.value = false;
    }

    // 保存本次查询
    prevSearchParams.value = cloneDeep(searchParams);
    if (props.after) {
      await nextTick(async () => await props.after?.(searchParams));
    }

    // 保存搜索条件
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    for (let [_, item] of Object.entries(fieldInterfaceMap)) {
      item.persist?.();
    }
  });
};

////////////////////////////////////////////////////////
// 从持久化中加载布局
let layoutDatas = getLayout<{ id: string; show: boolean }[]>(
  'search',
  ['fields', props.name],
  [],
  (v) => !!v.push
);
// 用于子元素注册, 执行重新搜索条件
const control = protectedReactive();

function saveLayoutData() {
  saveLayout(
    'search',
    ['fields', props.name],
    layoutDatas.map((e) => ({ id: e.id, show: e.show }))
  );
}

// field 提供的接口
interface FieldInterface {
  label: string; // 字段名
  hideNotify: (hidden: boolean) => void; // 隐藏通知
  reset: () => void; // 重置输入
  set: (val: any, params: PlainObject) => void; // 设置参数
  persist?: () => void; // 保持搜索条件
  defaultHide?: boolean; // 默认隐藏
}

// 用于触发变更重算
const fieldChangeRef = ref(0);
const fieldInterfaceMap: Record<string, FieldInterface> = protectedReactive(
  {},
  (name: string, field: FieldInterface) => {
    if (!field) {
      return;
    }

    const p = find(layoutDatas, (e) => e.id == name);
    if (!p) {
      const show = !field.defaultHide;
      // 新增字段
      layoutDatas.push({ id: name, show });
      field.hideNotify(!props.useAdvancedSearch && !show);
      ++fieldChangeRef.value;
      return;
    }
    field.hideNotify(!props.useAdvancedSearch && !p.show);
  }
);

// 隐藏项
const isHidden = function (name: string) {
  const p = [fieldChangeRef.value] && find(layoutDatas, (e) => e.id == name);
  if (p) return !p.show;
  return false;
};

const showItem = (name: string, visible: boolean, save = true) => {
  if (name) {
    const p = find(layoutDatas, (e) => e.id == name);
    p!.show = visible;
    fieldInterfaceMap[name]?.hideNotify?.(!props.useAdvancedSearch && !visible);
    ++fieldChangeRef.value;
  }

  if (save) {
    saveLayoutData();
  }
};

const resetCommand = async () => {
  prevSearchParams.value = undefined;

  // 重置所有input条件
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  for (let [_, item] of Object.entries(fieldInterfaceMap)) {
    item.reset?.();
  }

  await searchCommand();
};

const setAndQueryCommand = async (params: PlainObject, notSearch?: boolean) => {
  // 此处params未必同查询条件中的参数一致
  // 暴露出去的 setParams 方法也会调用此方法 但是 setParams 只会传入欲修改的参数 而不是完整的参数
  // 将参数信息回显到组件中
  for (let [name, val] of Object.entries(params)) {
    fieldInterfaceMap[name]?.set(val, params);
  }

  if (!notSearch) {
    await searchCommand();
  }
};

const moveItem = (name: string, index: number) => {
  const oldIndex = findIndex(layoutDatas, (e) => e.id == name);

  // 调整数组
  arrayMove(layoutDatas, oldIndex, index);

  // 保存配置
  saveLayoutData();

  // 触发ui移动
  control.move?.(name, index);

  ++fieldChangeRef.value;
};

// 恢复配置
const restore = () => {
  deleteLayout('search', ['fields', props.name]);
  layoutDatas = [];

  control.reload?.();

  ++fieldChangeRef.value;
};

///////////////////////////////////////////////////////////////////////////

// 给子组件share数据
shareData('search', {
  name: props.name,

  // 查询条件参数
  params: searchParams,

  // 查询条件参数
  pagination: pagination,

  // SearchTable注册search执行函数
  table: searchTable,

  // submit按钮调用此方法执行
  submit: searchCommand,

  // 重置函数
  reset: resetCommand,

  // 设置条件并查询
  set: setAndQueryCommand,

  loading, // 搜索中状态

  scrollContainer: protectedReactive(), // 用于滚动吸顶

  layout: {
    // field注册进来的接口
    fieldInterfaceMap,

    // 可控制隐藏指定项
    showItem,

    // 判断是否隐藏
    isHidden,

    // 移动位置
    moveItem,

    // 恢复初始状态
    restore,

    // SearchCondition 中注册下来, 移动条件项
    control // {"move": () => void, "reload": () => void, "sorted":{name:, label: } }
  }
});

// 共cmdbar使用, cmdbar是可复用的， 不一定在search环境下使用
shareData('table', {
  name: props.name,

  // 包含api, columns描述, 由searchTable注册上来
  ctx: protectedReactive(),

  // 由cmdbar注册上来，searchTable中contextmenu调用
  cmd: protectedReactive(),

  // 组合列定义
  // field => {subField => title}
  complexLabels: protectedReactive<Record<string, Record<string, string>>>(),

  // 组合列: visible列名
  // field => {subField => visible}
  complexVisibles: protectedReactive<Record<string, Record<string, boolean>>>(),

  // 扩展配置
  //  showColumnConfig 显示列配置
  //  disabledEdit 禁止单元格中edit
  setting: protectedReactive()
});

// 执行首次搜索
if (props.autoSearch) {
  onMounted(() => searchCommand());
}
</script>

<style scoped>
.sp-cont {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-y: auto;

  --easy-cmdbar-border-color: white;
}

.easy-popup .sp-cont {
  --easy-cmdbar-border-color: #e6e9ef;

  background-color: white;
}
</style>
